import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { getBoothById }      from '../../../api/boothApi';
import { CONTENT_TYPES }    from '../../../constants/contentTypes';
import PortalDetailPanel     from './PortalDetailPanel';
import BoothMediaDetailPanel from './BoothMediaDetailPanel';
import CustomBoothItemDetailPanel from './CustomBoothItemDetailPanel';

const ACCORDION_SX = {
  border: '1px solid #fecaca',
  borderRadius: '8px !important',
  mb: 1,
  overflow: 'hidden',
  '&:before': { display: 'none' },
};

function DeletionSide({ name }) {
  return (
    <Box sx={{ p: 2.5 }}>
      <Alert severity="error" icon={<DeleteForeverIcon sx={{ fontSize: 18 }} />} sx={{ borderRadius: 2 }}>
        <Typography variant="body2" fontWeight={700} mb={0.5}>
          Will be deleted
        </Typography>
        <Typography variant="caption">
          <strong>{name || 'This item'}</strong> will be permanently deleted when the booth deletion is approved.
        </Typography>
      </Alert>
    </Box>
  );
}

function PortalCascadeAccordion({ item, portalTypeMap }) {
  const name = item.localizedPairs?.values?.[0]?.value || '—';

  return (
    <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
        sx={{ minHeight: 44, px: 2, py: 0.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', overflow: 'hidden' }}>
          <Chip
            label="PORTAL"
            size="small"
            sx={{
              bgcolor: '#dbeafe', color: '#1e40af', fontSize: 9,
              fontWeight: 700, height: 20, flexShrink: 0,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {name}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0, borderTop: '1px solid #fee2e2' }}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ flex: 1, borderRight: '1px solid #e2e8f0' }}>
            <PortalDetailPanel data={item} diffKeys={new Set()} side="current" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <DeletionSide name={name} />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function MediaCascadeAccordion({ item }) {
  const name = item.textLocalizations?.[0]?.value || '—';

  return (
    <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
        sx={{ minHeight: 44, px: 2, py: 0.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', overflow: 'hidden' }}>
          <Chip
            label="BOOTH MEDIA"
            size="small"
            sx={{
              bgcolor: '#f0fdf4', color: '#15803d', fontSize: 9,
              fontWeight: 700, height: 20, flexShrink: 0,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {name}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0, borderTop: '1px solid #fee2e2' }}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ flex: 1, borderRight: '1px solid #e2e8f0' }}>
            <BoothMediaDetailPanel data={item} diffKeys={new Set()} side="current" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <DeletionSide name={name} />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function CustomItemCascadeAccordion({ item }) {
  const name = item.localizedPairs?.values?.[0]?.value || '—';

  return (
    <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
        sx={{ minHeight: 44, px: 2, py: 0.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', overflow: 'hidden' }}>
          <Chip
            label="CUSTOM ITEM"
            size="small"
            sx={{
              bgcolor: '#ede9fe', color: '#6d28d9', fontSize: 9,
              fontWeight: 700, height: 20, flexShrink: 0,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {name}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0, borderTop: '1px solid #fee2e2' }}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ flex: 1, borderRight: '1px solid #e2e8f0' }}>
            <CustomBoothItemDetailPanel data={item} diffKeys={new Set()} side="current" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <DeletionSide name={name} />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function CascadeList({ items, portalTypeMap }) {
  const portals     = items.filter((i) => i.type === CONTENT_TYPES.PORTAL);
  const media       = items.filter((i) => i.type === CONTENT_TYPES.MEDIA);
  const customItems = items.filter((i) => i.type === CONTENT_TYPES.CUSTOM);
  const total       = portals.length + media.length + customItems.length;

  if (total === 0) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <DeleteForeverIcon sx={{ fontSize: 15, color: '#dc2626' }} />
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: '#dc2626', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}
        >
          Also deleted with this booth
        </Typography>
        <Chip
          label={`${total} item${total !== 1 ? 's' : ''}`}
          size="small"
          sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 10, height: 18, '& .MuiChip-label': { px: 0.75 } }}
        />
      </Box>
      {portals.map((p) => (
        <PortalCascadeAccordion key={`portal-${p.id}`} item={p} portalTypeMap={portalTypeMap} />
      ))}
      {media.map((m) => (
        <MediaCascadeAccordion key={`media-${m.id}`} item={m} />
      ))}
      {customItems.map((c) => (
        <CustomItemCascadeAccordion key={`custom-${c.id}`} item={c} />
      ))}
    </Box>
  );
}

// Collapsible portals + booth_media that will be cascade-deleted with the booth.
// Data source: proposedSnapshot.boothItems (present in new proposals).
// Fallback: live-fetches via getBoothById when boothItems is absent from snapshot (old proposals).
export default function BoothDeleteCascadeSection({ boothItems, boothId, portalTypeMap }) {
  const [resolvedItems, setResolvedItems] = useState(null);
  const [fetching,      setFetching]      = useState(false);

  useEffect(() => {
    if (Array.isArray(boothItems)) {
      setResolvedItems(boothItems);
      return;
    }
    if (!boothId) return;
    setFetching(true);
    getBoothById(boothId)
      .then((booth) => setResolvedItems(booth?.boothItems ?? []))
      .catch(() => setResolvedItems([]))
      .finally(() => setFetching(false));
  }, [boothItems, boothId]);

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={14} sx={{ color: '#dc2626' }} />
        <Typography variant="caption" sx={{ color: '#6b7280' }}>Loading items…</Typography>
      </Box>
    );
  }

  if (!resolvedItems) return null;

  return <CascadeList items={resolvedItems} portalTypeMap={portalTypeMap} />;
}
